import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, Input, OnChanges, SimpleChanges, signal } from '@angular/core';
import { CodelistTranslationService } from '../../../services/codelist-translation.service';
import { EinvoiceViewerContextService } from '../../../services/einvoice-viewer-context.service';
import { getEmbeddedContentPreview } from '../../../utils/embedded-content-policy';
import { createSelectionFromNode, EInvoiceFieldSelection } from '../../../utils/provenance-index';
import { EMPTY_SCHEMA_LABEL_INDEX, normalizeModelPath } from '../../../utils/schema-label-index';
import { DocumentSearchEntry, EINVOICE_DOCUMENT_SEARCH_STATE, EinvoiceDocumentSearchState } from '../document-search-state';

interface FieldRenderState {
  value: string;
  provenanceTerm?: string;
  selectionEntry?: EInvoiceFieldSelection;
}

const EMPTY_FIELD_RENDER_STATE: FieldRenderState = {
  value: '',
  provenanceTerm: undefined,
  selectionEntry: undefined
};

let nextSearchFieldId = 0;

@Component({
    selector: 'app-field',
    templateUrl: './field.component.html',
    styleUrls: ['./field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FieldComponent {
  private einvoiceViewerContextService = inject(EinvoiceViewerContextService, { optional: true });
  private readonly documentSearchState = inject(EINVOICE_DOCUMENT_SEARCH_STATE, { optional: true });
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private lastScrolledModelPath: string | undefined;
  private readonly fieldRenderState = signal<FieldRenderState>(EMPTY_FIELD_RENDER_STATE);

  @Input() document: any | undefined;
  @Input() path: string | undefined;
  @Input() directValue: string | undefined;
  @Input() numberFormat: string | undefined;
  @Input() dateFormat: string | undefined;
  @Input() i18nNamespace: string | string[] | undefined;
  @Input() renderAsEmail: boolean | undefined;
  @Input() emailSubject: string | undefined;
  @Input() pathIfEmpty: string | undefined;
  @Input() label: string | undefined;
  @Input() prefix: string | undefined;
  @Input() suffix: string | undefined;

  readonly selectedModelPaths = this.einvoiceViewerContextService?.selectedModelPaths ?? signal<ReadonlySet<string>>(new Set<string>());
  readonly activeModelPath = this.einvoiceViewerContextService?.activeModelPath ?? signal<string | undefined>(undefined);
  readonly schemaLabels = this.einvoiceViewerContextService?.schemaLabels ?? signal(EMPTY_SCHEMA_LABEL_INDEX);
  readonly renderState = this.fieldRenderState.asReadonly();
  readonly tooltipText = computed(() => this.buildTooltipText(this.fieldRenderState(), this.schemaLabels()));
  readonly searchFieldId = `document-field-${++nextSearchFieldId}`;
  readonly isSearchMatch = computed(() => this.documentSearchState?.searchResultIds().has(this.searchFieldId) ?? false);
  readonly isCurrentSearchMatch = computed(() => this.documentSearchState?.currentSearchResultId() === this.searchFieldId);

  constructor(
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private codelistTranslationService: CodelistTranslationService
  ) {
    effect(() => {
      const activeModelPath = this.einvoiceViewerContextService?.activeModelPath();
      const modelPath = this.fieldRenderState().selectionEntry?.modelPath;
      if (!activeModelPath || activeModelPath !== modelPath || this.lastScrolledModelPath === activeModelPath) {
        return;
      }

      this.lastScrolledModelPath = activeModelPath;
      requestAnimationFrame(() => this.elementRef.nativeElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' }));
    });
  }

  ngOnChanges(_: SimpleChanges): void {
    this.fieldRenderState.set(this.buildFieldRenderState());
  }

  select(event: MouseEvent): void {
    const selectionEntry = this.fieldRenderState().selectionEntry;
    if (!selectionEntry || !this.einvoiceViewerContextService) {
      return;
    }

    event.stopPropagation();
    this.einvoiceViewerContextService.select(selectionEntry, event.ctrlKey || event.metaKey);
  }

  getSearchEntry(): DocumentSearchEntry | undefined {
    const state = this.fieldRenderState();
    const searchTexts = [state.selectionEntry?.term, state.provenanceTerm, state.value]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    if (searchTexts.length === 0) {
      return undefined;
    }

    return {
      id: this.searchFieldId,
      searchTexts
    };
  }

  scrollIntoView(): void {
    this.elementRef.nativeElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  }

  private getResolvedPath(): string | undefined {
    if (this.directValue || this.path === undefined) {
      return undefined;
    }

    if (this.resolvePath(this.path) !== undefined) {
      return this.path;
    }

    if (this.pathIfEmpty !== undefined) {
      return this.pathIfEmpty;
    }

    return undefined;
  }

  private buildFieldRenderState(): FieldRenderState {
    const resolvedPath = this.getResolvedPath();
    const resolvedField = resolvedPath ? this.resolveFieldForTooltip(resolvedPath) : undefined;
    const selectionEntry = resolvedField ? createSelectionFromNode(resolvedField) : undefined;

    return {
      value: this.buildDisplayValue(resolvedPath, resolvedField),
      provenanceTerm: resolvedField?.$provenance?.term as string | undefined,
      selectionEntry
    };
  }

  private resolvePath(path: string): any {
    // Supports array indices in bracket notation, e.g. "Seller.SellerIdentifier[0].value".
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let value = this.document;
    if (value === undefined) {
      return undefined;
    }

    for (const part of parts) {
      if (part.length === 0) {
        continue;
      }
      if (value == null || value[part] === undefined) {
        return undefined;
      }
      value = value[part];
    }

    return value;
  }

  private resolveFieldForTooltip(path: string): any {
    const resolvedValue = this.resolvePath(path);
    if (resolvedValue?.$provenance != null) {
      return resolvedValue;
    }

    const parts = path.split('.');
    if (parts.length <= 1) {
      return this.document;
    }

    return this.resolvePath(parts.slice(0, -1).join('.'));
  }

  private buildDisplayValue(resolvedPath: string | undefined, resolvedField: any): string {
    if (this.directValue) {
      return this.directValue;
    }

    if (this.path === undefined) {
      return '';
    }

    let value = resolvedPath ? this.resolvePath(resolvedPath) : undefined;
    if (value === undefined) {
      return '';
    }

    const embeddedContentPreview = getEmbeddedContentPreview(value, resolvedField);
    if (embeddedContentPreview?.collapsed) {
      return this.addPrefixSuffix(embeddedContentPreview.displayValue);
    }

    if (this.i18nNamespace !== undefined) {
      const translation = Array.isArray(this.i18nNamespace)
        ? this.codelistTranslationService.translateCodelistItemByNSArray(this.i18nNamespace, value)
        : this.codelistTranslationService.translateCodelistItem(this.i18nNamespace, value);
      return this.addPrefixSuffix(translation);
    }

    if (this.numberFormat) {
      value = this.decimalPipe.transform(value, this.numberFormat) ?? '';
    } else if (this.dateFormat) {
      value = this.datePipe.transform(value, this.dateFormat) ?? '';
    }

    const normalizedValue = this.addPrefixSuffix(String(value))
      .replace(/\s*(?:\r\n|\r|\n)\s*/g, '\n');

    return normalizedValue;
  }

  addPrefixSuffix(value: string): string {
    if (this.prefix && this.prefix.length > 0) {
      value = this.prefix + value;
    }
    if (this.suffix && this.suffix.length > 0) {
      value = value + this.suffix;
    }
    return value;
  }

  private buildTooltipText(state: FieldRenderState, schemaLabels: typeof EMPTY_SCHEMA_LABEL_INDEX): string {
    const selectionEntry = state.selectionEntry;
    const normalizedModelPath = selectionEntry?.modelPath ? normalizeModelPath(selectionEntry.modelPath) : undefined;
    const schemaLabel = (normalizedModelPath ? schemaLabels.byModelPath.get(normalizedModelPath) : undefined)
      ?? (selectionEntry?.term ? schemaLabels.byTerm.get(selectionEntry.term) : undefined);

    const heading = [state.provenanceTerm ?? schemaLabel?.term, schemaLabel?.label]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .join(' ')
      .trim();
    const description = schemaLabel?.description?.trim();

    if (heading && description) {
      return `${heading}: ${description}`;
    }

    return heading || description || '';
  }

}
